import { useEffect, useMemo } from 'react';

import type { IUserFront } from '@api/routers/user/service';
import { useAccount } from 'wagmi';

import { setAuth, useAuth } from '@web/utils/auth/manager';
import { trpcReact } from '@web/utils/trpc/trpcReact';

interface IDisconnected {
  status: 'disconnected';
  user: null;
}

interface IPending {
  status: 'pending';
  user: undefined;
}

export interface IConnectedUser extends IUserFront {
  displayWallet: `0x${string}`;
}

interface IConnected {
  status: 'connected';
  user: IConnectedUser;
}

/**
 * Same as IConnected, but user data is enforced
 */
interface ICompleted {
  status: 'connected';
  user: ICompletedUser;
}

export type ICompletedUser = Required<IConnectedUser>;

export function useCurrentUser(): IDisconnected | IPending | IConnected {
  const auth = useAuth();
  const user = trpcReact.user.me.useQuery(undefined, {
    enabled: !!auth,
    refetchInterval: 15_000,
    retry: 1,
  });
  const wallet = useAccount();

  // If the stored credentials can't be exchanged for a user (expired token,
  // wrong-shape token from a previous platform version, rotated JWT secret,
  // refresh-token row missing in DB, etc.), drop them. Without this, the UI
  // would loop forever on the splash screen because the status sits at
  // `pending` whenever `auth` is truthy but `user.data` never arrives.
  useEffect(() => {
    if (auth && user.isError) {
      setAuth(null);
    }
  }, [auth, user.isError]);

  return useMemo(() => {
    if (!auth || user.isError) {
      // Not authenticated, or our stored token was rejected by the backend.
      return { status: 'disconnected', user: null };
    }

    if (!user.data) {
      // User is authenticated, but data is not available yet
      return {
        status: 'pending',
        user: undefined,
      };
    }

    const displayWallet =
      wallet.status === 'connected' ? wallet.address : user.data.wallets[0];

    if (!displayWallet) {
      return { status: 'pending', user: undefined };
    }

    return {
      status: 'connected',
      user: {
        ...user.data,
        displayWallet,
      },
    };
  }, [auth, user.data, user.isError, wallet.status, wallet.address]);
}

export function isUserCompleted(
  currentUser: ReturnType<typeof useCurrentUser>,
): currentUser is ICompleted {
  return currentUser.status === 'connected' && !!currentUser.user.twitter;
}

export function useCurrentCompletedUserOrThrow(): ICompleted {
  const currentUser = useCurrentCompletedUser();

  if (!currentUser) {
    throw new Error(
      'Usage of useCurrentCompletedUserOrThrow outside connected component scope.',
    );
  }

  return currentUser;
}

export function useCurrentCompletedUser(): ICompleted | undefined {
  const currentUser = useCurrentUser();

  return isUserCompleted(currentUser) ? currentUser : undefined;
}
